import { useAuth } from '@/contexts/auth-context';
import { SUBSCRIPTION_QUERY_KEY } from '@/hooks/queries/use-subscription-query';
import { SUCCESS_FEE_TERMS_QUERY_KEY } from '@/hooks/queries/use-success-fee-terms';
import { usePusher } from '@/hooks/usePusher';
import api from '@/libs/axiosInstance';
import type { ApiResponse, SuccessFeeMigrationStatus } from '@fluenceur/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

export const SUCCESS_FEE_MIGRATION_QUERY_KEY = ['success-fee-migration'] as const;

const fetchSuccessFeeMigration = async (): Promise<SuccessFeeMigrationStatus> => {
  const response = await api.get<ApiResponse<SuccessFeeMigrationStatus>>(
    '/dashboard/billing/success-fee-migration'
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to fetch the success fee migration status');
  }

  return response.data.data;
};

interface UseSuccessFeeMigrationOptions {
  enabled?: boolean;
  /**
   * Subscribe to the brand channel for the activation event. Off on the locked
   * dashboard, whose only allowed calls are the ones that unlock it: the Pusher
   * authorization endpoint is not one of them, so subscribing there would only
   * buy a retry loop against a refusal.
   */
  realtime?: boolean;
}

/**
 * Read the success-fee billing migration status for the current brand.
 *
 * Shopify tells us the replacement is live through the `app_subscriptions/update`
 * webhook, which is also what fires the existing `subscription-activated` Pusher
 * event. Subscribing to it here is the only way the card flips to `active`
 * without the merchant reloading the page.
 */
export function useSuccessFeeMigrationQuery(options: UseSuccessFeeMigrationOptions = {}) {
  const { enabled = true, realtime = true } = options;
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: SUCCESS_FEE_MIGRATION_QUERY_KEY,
    queryFn: fetchSuccessFeeMigration,
    staleTime: 1000 * 60,
    retry: 1,
    enabled,
  });

  const handleSubscriptionActivated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: SUCCESS_FEE_MIGRATION_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
    // The rollout notice reads its deadline off the terms query, so an
    // activation that did not reach it would leave the banner asking for a step
    // that is done.
    queryClient.invalidateQueries({ queryKey: SUCCESS_FEE_TERMS_QUERY_KEY });
  }, [queryClient]);

  const brandId = user?.currentBrandId;

  usePusher({
    channelName: brandId ? `private-brand-${brandId}` : '',
    eventName: 'subscription-activated',
    callback: handleSubscriptionActivated,
    enabled: enabled && realtime && !!brandId,
  });

  return query;
}
